import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    const bootstrap = useCallback(async () => {
        const t = localStorage.getItem("wfos_token");
        if (!t) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setCompany(data.company);
        } catch (e) {
            localStorage.removeItem("wfos_token");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("wfos_token", data.token);
        setUser(data.user);
        setCompany(data.company);
        return data;
    };

    const signup = async (payload) => {
        const { data } = await api.post("/auth/signup", payload);
        localStorage.setItem("wfos_token", data.token);
        setUser(data.user);
        setCompany(data.company);
        // Sprint Q-1 · silent-default the user's preferred language from the
        // browser's navigator.language. If the base code (e.g. `es` from `es-MX`)
        // is a supported catalog entry, patch it in. English/unknown → no-op.
        try {
            const browserLang = (navigator.language || "en").toLowerCase();
            const base = browserLang.split("-")[0];
            if (base && base !== "en") {
                const { data: catalog } = await api.get("/languages");
                const supported = new Set((catalog?.languages || []).map((l) => l.code));
                const candidate = supported.has(browserLang)
                    ? browserLang
                    : supported.has(base)
                    ? base
                    : null;
                if (candidate) {
                    await api.patch("/users/me/preferences", { preferred_language: candidate });
                }
            }
        } catch {
            // Silent — an unavailable language endpoint should not block signup.
        }
        return data;
    };

    const logout = () => {
        localStorage.removeItem("wfos_token");
        setUser(null);
        setCompany(null);
    };

    return (
        <AuthCtx.Provider value={{ user, company, loading, login, signup, logout }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const v = useContext(AuthCtx);
    if (!v) throw new Error("useAuth must be used within AuthProvider");
    return v;
}
